"use client";
import React, { useMemo, useRef } from "react";
import Image from "next/image";
import LeftIcon from "@/assets/icons/arrow-left.svg?react";
import RightIcon from "@/assets/icons/arrow-right.svg?react";
import { getLangFromLocale } from "@/utils/common";
import Checkbox from "@/components/ui/checkbox/Checkbox";
import { useParams } from "next/navigation";
import { FacetItem, PopularBrandItem } from "@/common/lib/types";
import { cn } from "@/lib/tailwind";
import { getCloudinaryURL } from "@/utils/cloudinary";
import NoImageSrc from "@/assets/images/no_image.svg";

export interface PopularBrandsProps {
  col?: number;
  popularBrands: { title: string; brands: PopularBrandItem[] };
  selectedValues: string[];
  filteredBrands: FacetItem[];
  handleChange: (value: string) => void;
}

const PopularBrands = ({
  col = 1,
  popularBrands,
  selectedValues,
  filteredBrands,
  handleChange,
}: PopularBrandsProps) => {
  const params = useParams();
  const lang = useMemo(() => getLangFromLocale(params?.locale as string), [params?.locale]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const scrollLeft = () => {
    if (containerRef?.current) {
      containerRef?.current?.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (containerRef?.current) {
      containerRef?.current?.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const filteredPopularBrand = useMemo(() => {
    const availableBrands = filteredBrands.reduce(
      (acc, cur) => [...acc, cur.value],
      [] as string[],
    );
    return (
      (popularBrands?.brands || []).filter((item) => availableBrands.includes(item?.name)) || []
    );
  }, [popularBrands?.brands, filteredBrands]);

  if (filteredPopularBrand.length === 0) return <></>;

  return (
    <div>
      <p className={`text-sm ${col == 1 ? "" : "mb-4 font-semibold"}`}>
        {popularBrands?.title || ""}
      </p>
      <div className="flex items-center">
        {col === 1 && (
          <button onClick={lang === "ar" ? scrollRight : scrollLeft} className="p-2">
            {lang === "ar" ? <RightIcon /> : <LeftIcon />}
          </button>
        )}
        <div
          ref={containerRef}
          className={`no-scrollbar flex overflow-x-scroll scroll-smooth ${col === 1 ? "my-4 flex max-w-96 gap-2" : "w-full flex-col gap-3 pr-4"}`}
        >
          {filteredPopularBrand.map((brand, index) => {
            // const hasItems = filteredBrands.some((item) => item?.value === brand?.name);
            // if (!hasItems) return null;
            return (
              <div
                key={index}
                className={cn(
                  "flex h-[60px] cursor-pointer items-center justify-between gap-1 rounded border px-4",
                  col === 1 ? "min-w-[130px]" : "min-w-[180px]",
                  // !hasItems && "cursor-not-allowed opacity-50",
                )}
                // onClick={() => (hasItems ? handleChange(brand?.name) : null)}
                onClick={() => handleChange(brand?.name)}
              >
                <Image
                  src={getCloudinaryURL(brand?.backgroundImage?.url) ?? NoImageSrc}
                  alt={brand?.slug}
                  width={80}
                  height={60}
                />
                <Checkbox
                  checked={selectedValues?.includes(brand?.name)}
                  onChange={() => {}}
                  className="h-4 w-4 border-tertiary-4 checked:bg-[#4285F4] hover:bg-white hover:checked:bg-[#4285F4] focus:checked:bg-[#4285F4]"
                />
              </div>
            );
          })}
        </div>
        {col === 1 && (
          <button onClick={lang === "ar" ? scrollLeft : scrollRight} className="p-2">
            {lang === "ar" ? <LeftIcon /> : <RightIcon />}
          </button>
        )}
      </div>
    </div>
  );
};

export default PopularBrands;
